import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks'

/**
 * Button Component
 * Based on UX Design Guidelines §7.1 - Button Patterns
 *
 * Variants:
 * - primary: Main actions (Blue #3182ce)
 * - secondary: Secondary actions (Gray border)
 * - ghost: Tertiary actions (No border)
 * - danger: Destructive actions (Red)
 *
 * Sizes:
 * - sm: Compact (32px height)
 * - md: Default (40px height)
 * - lg: Large (48px height)
 */

export type ButtonVariant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  isLoading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  children: ReactNode
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-primary-light',
    'hover:bg-accent',
    'active:bg-primary',
    'disabled:bg-gray-300 disabled:text-gray-500'
  ),
  secondary: cn(
    'bg-white text-gray-700 border border-gray-300',
    'hover:bg-gray-50 hover:border-gray-400',
    'active:bg-gray-100',
    'disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200'
  ),
  soft: cn(
    'bg-accent-muted text-accent border border-accent/30',
    'hover:bg-accent-muted-hover hover:border-accent/40',
    'active:bg-accent/[0.25]',
    'disabled:bg-accent/[0.04] disabled:text-primary/40 disabled:border-accent/10'
  ),
  ghost: cn(
    'bg-transparent text-gray-700',
    'hover:bg-gray-100',
    'active:bg-gray-200',
    'disabled:text-gray-400 disabled:bg-transparent'
  ),
  danger: cn(
    'bg-error',
    'hover:bg-red-600',
    'active:bg-red-700',
    'disabled:bg-red-200 disabled:text-red-100'
  ),
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-body-sm gap-1.5',
  md: 'h-10 px-4 text-body gap-2',
  lg: 'h-12 px-6 text-body gap-2.5',
}

const iconSizeStyles: Record<ButtonSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-5 h-5',
}

// Get text color style for variants that need explicit color override
const getTextColorStyle = (variant: ButtonVariant, isDisabled: boolean) => {
  if (isDisabled) return undefined
  if (variant === 'primary' || variant === 'danger') {
    return { color: '#edf2f7' } // gray-100 - softer than pure white
  }
  return undefined
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      isLoading = false,
      leftIcon,
      rightIcon,
      children,
      className,
      disabled,
      style,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation()
    const isDisabled = disabled || isLoading

    return (
      <button
        ref={ref}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center whitespace-nowrap',
          'font-semibold rounded-md',
          'transition-colors duration-small',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
          // Disabled cursor
          isDisabled && 'cursor-not-allowed',
          // Variant and size
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        style={{ ...getTextColorStyle(variant, isDisabled), ...style }}
        disabled={isDisabled}
        {...props}
      >
        {isLoading ? (
          <>
            <Loader2 className={cn('animate-spin', iconSizeStyles[size])} />
            <span>{t('common.loading')}</span>
          </>
        ) : (
          <>
            {leftIcon && (
              <span className={iconSizeStyles[size]}>{leftIcon}</span>
            )}
            {children}
            {rightIcon && (
              <span className={iconSizeStyles[size]}>{rightIcon}</span>
            )}
          </>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'
